/**
 * THE FIRE LIFE SAFETY CUT SHEET, ON SCREEN — read-only, D-027.
 *
 * The design authority, 2026-08-18:
 *
 *   *"The final FLS screen will merely be a read-only 'cut sheet' of everything
 *    related to fire life safety on one screen … it's just an aggregation … for
 *    REFERENCE."*
 *
 * So this renders `buildFlsCutSheet(doc)` — the SAME block the office stores on
 * the sealed revision and the report agent prints for the fire alarm company —
 * and nothing else. No control on it writes anything. Every line here was
 * answered somewhere else in the walk, and that is where it is changed; the
 * blanks at the bottom say where.
 *
 * ── Why the same function, and not a second rendering of the answers ────────
 * A surveyor standing in the lobby reading this before they close the survey
 * is the last person who can fix it. If the screen folded the answers one way
 * and the agent folded them another, the sheet they approved would not be the
 * sheet the company received. One function, read twice.
 *
 * ── Shape on a 430px screen ──────────────────────────────────────────────────
 * The template's lines, in the template's order, as label → value rows — the
 * shape a cut sheet has on paper — then the per-room, per-shaft and per-bank
 * rows underneath so the company can see WHICH pit has the smoke detector.
 * Pictures are thumbnails in the row they belong to, with the prompt they were
 * taken against as the caption. Values that are not on the record print as an
 * em dash, never as No: D-016's rule that the form does not invent an answer
 * holds on the sheet too.
 */

import { Fragment, useMemo, type ReactNode } from 'react';

import {
  buildFlsCutSheet,
  type CutSheetOpenItem,
  type CutSheetPhoto,
  type FlsCutSheet,
  type LandingSpan,
  type Presence,
} from '../../core/flsCutSheet';
import type { Contact, SurveyDocument } from '../../core/types';
import { useSurvey } from '../state/SurveyProvider';
import { useThumbnails } from './PhotoCapture';
import { Card, Eyebrow } from './Surfaces';

export interface FlsCutSheetViewProps {
  /** Defaults to the survey in context; passed explicitly by tests and previews. */
  doc?: SurveyDocument;
}

const NONE = '—';

function yesNo(value: boolean | null): string {
  if (value === null) return NONE;
  return value ? 'Yes' : 'No';
}

const PRESENCE_WORD: Record<Presence, string> = {
  yes: 'Yes',
  no: 'No',
  some: 'Some',
  unknown: NONE,
};

/** F16 / F18 — a Yes/No/Unknown `select`, printed the same way `yesNo` prints a bool. */
function selectWord(value: 'yes' | 'no' | 'unknown' | null): string {
  if (value === null) return NONE;
  return value === 'yes' ? 'Yes' : value === 'no' ? 'No' : 'Unknown';
}

/** M5's own option labels (src/core/catalog.ts) — this sheet reads the raw value it stored. */
const ROOM_LOCATION_LABEL: Record<string, string> = {
  overhead: 'Overhead',
  basement: 'Basement',
  adjacent_lowest: 'Adjacent — lowest landing',
  adjacent_top: 'Adjacent — top landing',
  remote: 'Remote',
};

function roomLocation(value: string | null): string {
  if (value === null) return NONE;
  return ROOM_LOCATION_LABEL[value] ?? value;
}

/** F15 — feet from the FACP to this room; 0 means the panel is in the room. */
function feet(value: number | null): string {
  return value === null ? NONE : `${value} ft`;
}

/** Amendment 8 (D-041) — a required photo not yet taken, and why, in the surveyor's own words. */
function blankLine(item: CutSheetOpenItem): string {
  return item.reason ? `No photo yet of ${item.label} — ${item.reason}` : `No photo yet of ${item.label}`;
}

function picks(value: Record<string, boolean> | null, labels: Record<string, string>): string {
  if (value === null) return NONE;
  const on = Object.entries(value)
    .filter(([, set]) => set)
    .map(([key]) => labels[key] ?? key);
  return on.length === 0 ? 'None' : on.join(', ');
}

function spans(value: LandingSpan[]): string {
  if (value.length === 0) return NONE;
  return value
    .map((span) =>
      span.fromLanding === span.toLanding
        ? `landing ${span.fromLanding}`
        : `landings ${span.fromLanding}–${span.toLanding}`,
    )
    .join('; ');
}

function contact(value: Contact | null): string {
  if (!value) return NONE;
  return [value.name, value.phone, value.email].filter(Boolean).join(' · ') || NONE;
}

function dateOf(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/* ───────────────────────────── primitives ───────────────────────────────── */

function Row({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div className="cut__row">
      <dt className="cut__k" data-legibility="helper">
        {label}
      </dt>
      <dd className="cut__v">{children}</dd>
    </div>
  );
}

function Rows({ children }: { children: ReactNode }): ReactNode {
  return <dl className="cut__rows">{children}</dl>;
}

function Photos({ photos, thumbs }: { photos: CutSheetPhoto[]; thumbs: Record<string, string> }): ReactNode {
  if (photos.length === 0) return null;
  return (
    <ul className="photos__grid cut__photos" aria-label="Pictures on this line">
      {photos.map((photo) => {
        const url = thumbs[photo.id];
        return (
          <li className="photo" key={photo.id}>
            {url ? (
              <img className="photo__img" src={url} alt={photo.label} />
            ) : (
              <span className="photo__img photo__img--none" aria-hidden="true">
                ▣
              </span>
            )}
            <span className="photo__state" data-legibility="display">
              {photo.label}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function Sub({ children }: { children: ReactNode }): ReactNode {
  return (
    <h3 className="cut__sub" data-legibility="display">
      {children}
    </h3>
  );
}

/* ──────────────────────────────── the sheet ─────────────────────────────── */

export function FlsCutSheetView({ doc }: FlsCutSheetViewProps): ReactNode {
  const context = useSurvey();
  const document = doc ?? context.doc;
  const sheet = useMemo<FlsCutSheet>(() => buildFlsCutSheet(document), [document]);
  // The sheet's photos are the document's own, re-described for the fire
  // alarm company; the thumbnails want the PhotoRefs themselves, because
  // where the bytes come from depends on the upload state (round 8, task 4).
  // Thumbnails, not originals (round 8.1, task 4): this sheet is read on the
  // phone, and its pictures are the same 168-px tiles the question grid draws.
  const photoRefs = useMemo(
    () =>
      sheet.photos.flatMap((photo) => {
        const ref = document.photos.find((candidate) => candidate.id === photo.id);
        return ref ? [ref] : [];
      }),
    [sheet, document],
  );
  const thumbs = useThumbnails(photoRefs);
  // The panel's own blanks (F2–F5) are the questions directly above this sheet
  // on the same screen; a link from here to here would be a control that does
  // nothing. They stay on the sheet the office receives (`open`), just not in
  // this sheet; the jump-list that once pointed at the other places is gone (D-033 R7).

  const relays = sheet.summary.relays;
  const grid: [string, FlsCutSheet['summary']['lobbies']][] = [
    ['Lobbies', sheet.summary.lobbies],
    ['Pit', sheet.summary.pit],
    ['Overhead', sheet.summary.overhead],
    ['Machine room', sheet.summary.machineRoom],
  ];

  return (
    <section className="cut" aria-label="Fire Life Safety cut sheet">
      <Eyebrow>Cut sheet — for the fire alarm company</Eyebrow>

      <Card as="section" ariaLabel="Job">
        <Sub>Job</Sub>
        <Rows>
          <Row label="Date">{dateOf(sheet.job.date)}</Row>
          <Row label="Job name">{sheet.job.name ?? NONE}</Row>
          <Row label="Surveyor">{sheet.job.surveyor ?? NONE}</Row>
          <Row label="Address">{sheet.job.address ?? NONE}</Row>
          <Row label="Elevators">{sheet.job.elevators ?? NONE}</Row>
          <Row label="Landings">{sheet.job.landings}</Row>
          <Row label="Machine rooms">{sheet.job.machineRooms}</Row>
          <Row label="Hoistways">{sheet.job.hoistways}</Row>
        </Rows>
      </Card>

      <Card as="section" ariaLabel="Existing panel">
        <Sub>Existing panel</Sub>
        <Rows>
          <Row label="Panel (make and model)">{sheet.panel.makeModel ?? NONE}</Row>
          <Row label="Current FLS service provider">{sheet.panel.serviceProvider ?? NONE}</Row>
          <Row label="Their contact">{contact(sheet.panel.serviceContact)}</Row>
          <Row label="Back boxes (one per landing)">{sheet.panel.backBoxes ?? NONE}</Row>
          <Row label="Job specific notes">{sheet.panel.notes ?? NONE}</Row>
          <Row label="Sprinkler system">{selectWord(sheet.summary.sprinklerSystem)}</Row>
          <Row label="Access">{sheet.panel.access ?? NONE}</Row>
        </Rows>
        <Photos photos={sheet.panel.photos} thumbs={thumbs} />
      </Card>

      <Card as="section" ariaLabel="Existing devices">
        <Sub>Existing devices</Sub>
        <table className="cut__grid">
          <thead>
            <tr>
              <th scope="col" data-legibility="helper">
                Area
              </th>
              <th scope="col" data-legibility="helper">
                Smokes
              </th>
              <th scope="col" data-legibility="helper">
                Heats
              </th>
              <th scope="col" data-legibility="helper">
                Sprinkled
              </th>
            </tr>
          </thead>
          <tbody>
            {grid.map(([area, devices]) => (
              <tr key={area}>
                <th scope="row" data-legibility="helper">
                  {area}
                </th>
                <td>{PRESENCE_WORD[devices.smoke]}</td>
                <td>{PRESENCE_WORD[devices.heat]}</td>
                <td>{PRESENCE_WORD[devices.sprinkled]}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="cut__foot" data-legibility="helper">
          "Some" means yes in part of the building; "—" means nobody has said yet.
        </p>
      </Card>

      <Card as="section" ariaLabel="Existing relays in the machine room">
        <Sub>Existing relays in the machine room</Sub>
        <Rows>
          <Row label="Primary">{PRESENCE_WORD[relays.primary]}</Row>
          <Row label="Alternate">{PRESENCE_WORD[relays.alternate]}</Row>
          <Row label="Flashing hat">{PRESENCE_WORD[relays.flashingHat]}</Row>
          <Row label="Shunt">{PRESENCE_WORD[relays.shunt]}</Row>
        </Rows>
      </Card>

      {sheet.machineRooms.map((room) => (
        <Card as="section" ariaLabel={`${room.name} — Fire Life Safety`} key={room.id}>
          <Sub>{room.name}</Sub>
          <Rows>
            <Row label="Cars">{room.cars.join(', ') || NONE}</Row>
            <Row label="Sprinkler in the room">{yesNo(room.sprinklered)}</Row>
            <Row label="Heat detector within 18″ of the head">{yesNo(room.heatDetectorAtSprinklerHead)}</Row>
            <Row label="Shunt relay">{yesNo(room.shuntRelay)}</Row>
            <Row label="Fire service relays">
              {picks(room.relays, {
                primary: 'Primary',
                alternate: 'Alternate',
                flashingHat: 'Flashing hat',
                shunt: 'Shunt',
              })}
            </Row>
            <Row label="Detectors in the room">
              {picks(room.devices, { smoke: 'Smoke', heat: 'Heat' })}
            </Row>
            <Row label="Location">{roomLocation(room.location)}</Row>
            <Row label="Distance to panel">{feet(room.facpDistanceFt)}</Row>
            <Row label="Recall installed">{selectWord(room.recallInstalled)}</Row>
            <Row label="Shunt disconnect">{room.shuntDisconnectLocation ?? NONE}</Row>
            <Row label="Main disconnect feed">{room.mainDisconnectFeed ?? NONE}</Row>
          </Rows>
          <Photos photos={room.photos} thumbs={thumbs} />
          {room.carPhotos
            .filter((car) => car.photos.length > 0)
            .map((car) => (
              <Fragment key={car.carId}>
                <Sub>{car.carLabel}</Sub>
                <Photos photos={car.photos} thumbs={thumbs} />
              </Fragment>
            ))}
        </Card>
      ))}

      {sheet.hoistways.map((hoistway) => (
        <Card
          as="section"
          ariaLabel={`${hoistway.roomName} · ${hoistway.name} — Fire Life Safety`}
          key={hoistway.id}
        >
          <Sub>
            {hoistway.roomName} · {hoistway.name}
          </Sub>
          <Rows>
            <Row label="Cars">{hoistway.cars.join(', ') || NONE}</Row>
            <Row label="Sprinklers in the hoistway">{yesNo(hoistway.overhead.sprinklered)}</Row>
            <Row label="Detectors at the top">
              {picks(hoistway.overhead.devices, { smoke: 'Smoke', heat: 'Heat' })}
            </Row>
            <Row label="Devices in the pit">
              {picks(hoistway.pit.devices, { sprinkler: 'Sprinkler', heat: 'Heat', smoke: 'Smoke' })}
            </Row>
          </Rows>
          <Photos photos={hoistway.photos} thumbs={thumbs} />
        </Card>
      ))}

      {sheet.lobbyGroups.map((group) => (
        <Card as="section" ariaLabel={`${group.roomName} · ${group.name} — Fire Life Safety`} key={group.id}>
          <Sub>
            {group.roomName} · {group.name} · {group.landings} landing{group.landings === 1 ? '' : 's'}
          </Sub>
          <Rows>
            <Row label="Cars">{group.cars.join(', ') || NONE}</Row>
            <Row label="Landings missing a smoke detector">
              {group.smokeDetectorsMissing === null
                ? NONE
                : group.smokeDetectorsMissing
                  ? spans(group.landingsMissingSmoke)
                  : 'None — every landing has one'}
            </Row>
            <Row label="Sprinklered lobbies">
              {group.sprinklered === null
                ? NONE
                : group.sprinklered
                  ? spans(group.sprinkleredLandings)
                  : 'None'}
            </Row>
            <Row label="Heat detectors in the sprinklered lobbies">
              {yesNo(group.sprinkleredLobbiesHaveHeat)}
            </Row>
            <Row label="Detector types">
              {picks(group.detectorTypes, { smoke: 'Smoke', heat: 'Heat', other: 'Other' })}
            </Row>
          </Rows>
          <Photos photos={group.photos} thumbs={thumbs} />
        </Card>
      ))}

      {(() => {
        const blanks = sheet.open.filter((item) => item.shotListId !== undefined);
        return blanks.length > 0 ? (
          <Card as="section" ariaLabel="Required photos not yet taken">
            <Sub>Blanks</Sub>
            <ul className="cut__notes">
              {blanks.map((item) => (
                <li className="cut__note" key={item.shotListId}>
                  <span className="cut__note-text" data-legibility="body">
                    {blankLine(item)}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        ) : null;
      })()}

      {sheet.notes.length > 0 ? (
        <Card as="section" ariaLabel="Notes for the fire alarm company">
          <Sub>Notes</Sub>
          <ul className="cut__notes">
            {sheet.notes.map((note, index) => (
              <li className="cut__note" key={`${note.createdAt}-${index}`}>
                <span className="cut__note-where" data-legibility="helper">
                  {note.sectionTitle}
                  {note.questionId ? ` · ${note.questionId}` : ''}
                </span>
                <span className="cut__note-text">{note.text}</span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </section>
  );
}
